import { useState } from "react";
import { Minus, Plus, X, Download, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart, FREE_SHIPPING } from "@/context/CartContext";
import { euro } from "@/lib/api";
import { CheckoutDialog } from "./CheckoutDialog";

const Line = ({ i }) => {
  const { setQty } = useCart();
  return (
    <li data-testid={`cart-line-${i.id}`} className="flex gap-4 py-5">
      <img src={i.image} alt={i.name} className="h-24 w-20 shrink-0 rounded-xl object-cover" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex justify-between gap-3">
          <p className="font-display text-base leading-snug">{i.name}</p>
          <button data-testid={`cart-remove-${i.id}`} onClick={() => setQty(i.id, 0)} aria-label="Retirer"><X className="h-4 w-4 text-[var(--muted)]" /></button>
        </div>
        {i.type === "numerique" && <p className="mt-1 flex items-center gap-1 text-xs text-[var(--muted)]"><Download className="h-3 w-3" /> Livraison numérique</p>}
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="flex items-center rounded-full border border-[var(--line)]">
            <button data-testid={`cart-dec-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-2"><Minus className="h-3 w-3" /></button>
            <span data-testid={`cart-qty-${i.id}`} className="w-6 text-center text-sm">{i.qty}</span>
            <button data-testid={`cart-inc-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-2"><Plus className="h-3 w-3" /></button>
          </div>
          <p className="text-sm font-medium">{euro(i.price * i.qty)}</p>
        </div>
      </div>
    </li>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, shipping, serviceFee, total, hasPhysical } = useCart();
  const [checkout, setCheckout] = useState(false);
  const pct = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="cart-drawer" className="flex w-full flex-col bg-[var(--bone)] p-0 sm:max-w-md">
          <div className="border-b border-[var(--line)] px-6 py-6">
            <SheetTitle className="font-display text-3xl font-light">Votre panier</SheetTitle>
            <SheetDescription className="text-sm text-[var(--muted)]">Paiement sécurisé par carte bancaire ou PayPal</SheetDescription>
          </div>
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
              <p className="font-display text-2xl italic">Votre panier est vide.</p>
              <button onClick={() => setOpen(false)} className="text-sm underline underline-offset-4">Continuer mes achats</button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6" data-lenis-prevent>
                {hasPhysical && (
                  <div className="mt-5 rounded-2xl bg-[var(--bone-2)] p-4">
                    <p className="flex items-center gap-2 text-xs">
                      <Truck className="h-4 w-4" />
                      {shipping > 0 ? `Plus que ${euro(FREE_SHIPPING - subtotal)} pour la livraison offerte` : "Livraison offerte débloquée"}
                    </p>
                    <div className="mt-3 h-1 overflow-hidden rounded-full bg-[var(--line)]">
                      <div className="h-full bg-[var(--terra)] transition-[width] duration-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
                <ul className="divide-y divide-[var(--line)]">{items.map((i) => <Line key={i.id} i={i} />)}</ul>
              </div>
              <div className="space-y-2 border-t border-[var(--line)] px-6 py-6 text-sm">
                <div className="flex justify-between"><span>Sous-total</span><span data-testid="cart-subtotal">{euro(subtotal)}</span></div>
                <div className="flex justify-between text-[var(--muted)]"><span>Livraison</span><span data-testid="cart-shipping">{shipping ? euro(shipping) : "Offerte"}</span></div>
                <div className="flex justify-between text-[var(--muted)]"><span>Frais de service</span><span data-testid="cart-service-fee">{euro(serviceFee)}</span></div>
                <div className="flex justify-between pt-2 font-display text-2xl"><span>Total</span><span data-testid="cart-total">{euro(total)}</span></div>
                <button data-testid="cart-checkout-button" onClick={() => setCheckout(true)}
                  className="mt-4 w-full rounded-full bg-[var(--ink)] py-4 text-sm text-[var(--bone)] transition-colors hover:bg-[var(--terra)]">
                  Commander
                </button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <CheckoutDialog open={checkout} onOpenChange={setCheckout} />
    </>
  );
};
